/**
 * 🔄 SISTEMA AUTOMATICO DI DISTRIBUZIONE ORE
 * 
 * Gestisce automaticamente la distribuzione e redistribuzione delle ore
 * lavorative tra più cantieri nella stessa giornata.
 * 
 * Funzionalità principali:
 * - Distribuzione automatica quando una persona è presente in più cantieri
 * - Reset automatico quando viene rimossa da cantieri
 * - Ridistribuzione automatica quando vengono aggiunti/rimossi cantieri
 * - Sincronizzazione automatica con workHours
 * 
 * @version 2.0.1
 * @author Sistema di Gestione Cantieri
 */

import { 
  collection, 
  getDocs, 
  updateDoc, 
  doc, 
  query, 
  where, 
  writeBatch 
} from 'firebase/firestore';
import { db } from '../firebase';
import { getWorkHoursForDate } from './workHoursIntegrationService';

// =====================================================
// 🎯 CONFIGURAZIONE SISTEMA
// =====================================================

const DISTRIBUTION_CONFIG = {
  MAX_STANDARD_HOURS: 8,
  MIN_DISTRIBUTION_CANTIERI: 2,
  UPDATE_DELAY: 500, // ms tra aggiornamenti
  BATCH_SIZE: 10 // Max rapportini per batch
};

// =====================================================
// 🔍 FUNZIONI DI RICERCA E ANALISI
// =====================================================

/**
 * Trova tutti i rapportini con una persona in una data specifica
 */
const findAllRapportiniWithEmployee = async (userId, date) => {
  try {
    console.log(`🔍 Ricerca rapportini per ${userId} in data ${date}`);
    
    const rapportiniRef = collection(db, 'rapportini');
    
    // 🔧 FIX: Converte la data in oggetto Date per la query Firestore
    const searchDate = typeof date === 'string' ? new Date(date) : date;
    console.log(`🔍 Data convertita per query: ${searchDate}`);
    
    const q = query(
      rapportiniRef, 
      where('data', '==', searchDate),
      where('stato', '==', 'Attivo') // Aggiunge filtro per rapportini attivi
    );
    const querySnapshot = await getDocs(q);
    
    const rapportiniWithEmployee = [];
    
    querySnapshot.forEach((doc) => {
      const rapportino = { id: doc.id, ...doc.data() };
      const personalePresente = rapportino.personalePresente || [];
      
      const personaFound = personalePresente.find(p => p.personaId === userId);
      if (personaFound) {
        console.log(`🎯 Trovato rapportino ${doc.id} con persona ${userId}`);
        rapportiniWithEmployee.push({
          ...rapportino,
          personaData: personaFound
        });
      }
    });
    
    console.log(`✅ Trovati ${rapportiniWithEmployee.length} rapportini con persona ${userId}`);
    return rapportiniWithEmployee;
    
  } catch (error) {
    console.error('❌ Errore ricerca rapportini:', error);
    throw error;
  }
};

/**
 * 🔧 FIX: Alias per compatibilità con i componenti esistenti
 */
const findRapportiniWithEmployee = findAllRapportiniWithEmployee;

/**
 * Analizza la situazione distribuzione per una persona
 */
const analyzeDistributionSituation = async (userId, date) => {
  const rapportini = await findAllRapportiniWithEmployee(userId, date);
  
  if (rapportini.length === 0) {
    return {
      action: 'none',
      rapportini: [],
      needsDistribution: false,
      needsReset: false,
      message: 'Nessun rapportino trovato'
    };
  }
  
  if (rapportini.length === 1) {
    const hasDistribution = rapportini[0].personaData.distributedHours === true;
    
    return {
      action: hasDistribution ? 'reset' : 'none',
      rapportini: rapportini,
      needsDistribution: false,
      needsReset: hasDistribution,
      message: hasDistribution ? 
        'Singolo cantiere con distribuzione - reset necessario' :
        'Singolo cantiere - nessuna azione necessaria'
    };
  }
  
  // Più cantieri
  const hasAnyDistribution = rapportini.some(r => r.personaData.distributedHours === true);
  const allHaveDistribution = rapportini.every(r => r.personaData.distributedHours === true);
  
  return {
    action: 'distribute',
    rapportini: rapportini,
    needsDistribution: true,
    needsReset: false,
    hasIncompleteDistribution: hasAnyDistribution && !allHaveDistribution,
    message: `${rapportini.length} cantieri - distribuzione necessaria`
  };
};

// =====================================================
// 🧮 FUNZIONI DI CALCOLO DISTRIBUZIONE
// =====================================================

/**
 * Calcola la distribuzione ottimale delle ore
 */
const calculateOptimalDistribution = (oreStandard, oreStraordinario, numCantieri) => {
  // Le ore possono arrivare come testo: "8" + "0" concatenerebbe in "80"
  oreStandard = parseFloat(oreStandard) || 0;
  oreStraordinario = parseFloat(oreStraordinario) || 0;
  const oreTotali = oreStandard + oreStraordinario;
  
  console.log(`📊 Calcolo distribuzione: ${oreTotali}h su ${numCantieri} cantieri`);
  
  // Distribuzione base
  const oreBasePerCantiere = Math.floor(oreTotali / numCantieri);
  const oreResto = oreTotali % numCantieri;
  
  const distribuzioneOre = [];
  
  for (let i = 0; i < numCantieri; i++) {
    let oreAssegnate = oreBasePerCantiere;
    
    // Distribuisci il resto ai primi cantieri
    if (i < oreResto) {
      oreAssegnate += 1;
    }
    
    // Calcola standard vs straordinario
    const oreStandardCantiere = Math.min(DISTRIBUTION_CONFIG.MAX_STANDARD_HOURS, oreAssegnate);
    const oreStraordinarioCantiere = Math.max(0, oreAssegnate - DISTRIBUTION_CONFIG.MAX_STANDARD_HOURS);
    
    distribuzioneOre.push({
      cantiereIndex: i,
      oreTotali: oreAssegnate,
      oreStandard: oreStandardCantiere,
      oreStraordinario: oreStraordinarioCantiere
    });
  }
  
  return {
    distribuzioneOre,
    numCantieri,
    distribuzioneTotale: {
      oreTotali,
      oreStandard,
      oreStraordinario
    }
  };
};

/**
 * Recupera le ore originali dal workHours
 */
const getOriginalHours = async (userId, date) => {
  try {
    // Usa respectDistribution = false per avere le ore originali
    const hoursData = await getWorkHoursForDate(userId, date, false, null);
    
    if (!hoursData.found || hoursData.isSpecialDay) {
      return null;
    }
    
    return {
      oreTotali: hoursData.oreLavorate,
      oreStandard: hoursData.oreStandard,
      oreStraordinario: hoursData.oreStraordinario
    };
  } catch (error) {
    console.error('❌ Errore recupero ore originali:', error);
    return null;
  }
};

// =====================================================
// 🔄 FUNZIONI DI AGGIORNAMENTO
// =====================================================

/**
 * Aggiorna un rapportino con ore distribuite
 */
const updateRapportinoWithDistribution = async (rapportino, userId, distributedHours, cantiereIndex) => {
  try {
    const oreDistribuite = distributedHours.distribuzioneOre[cantiereIndex];
    
    const updatedPersonale = rapportino.personalePresente.map(persona => {
      if (persona.personaId !== userId) return persona;
      
      const costoOrario = parseFloat(persona.costoOrario) || 0;
      const nuovoTotale = oreDistribuite.oreTotali * costoOrario;
      
      return {
        ...persona,
        oreLavorate: oreDistribuite.oreTotali,
        oreStandard: oreDistribuite.oreStandard,
        oreStraordinario: oreDistribuite.oreStraordinario,
        totale: nuovoTotale,
        notePersonale: `Ore distribuite: ${oreDistribuite.oreTotali}h su ${distributedHours.numCantieri} cantieri`,
        distributedHours: true,
        distributionDetails: {
          originalTotal: distributedHours.distribuzioneTotale.oreTotali,
          originalStandard: distributedHours.distribuzioneTotale.oreStandard,
          originalOvertime: distributedHours.distribuzioneTotale.oreStraordinario,
          assignedHours: oreDistribuite.oreTotali,
          assignedStandard: oreDistribuite.oreStandard,
          assignedOvertime: oreDistribuite.oreStraordinario,
          numCantieri: distributedHours.numCantieri,
          cantiereIndex: cantiereIndex,
          distributionDate: new Date().toISOString()
        }
      };
    });
    
    const updatedRapportino = {
      ...rapportino,
      personalePresente: updatedPersonale,
      distributedHoursUpdate: true,
      hasDistributedHours: true,
      distributionMetadata: {
        affectedPersonIds: [userId],
        distributionDate: new Date().toISOString(),
        numCantieri: distributedHours.numCantieri
      }
    };
    
    await updateDoc(doc(db, 'rapportini', rapportino.id), updatedRapportino);
    console.log(`✅ Aggiornato rapportino ${rapportino.id} con distribuzione`);
    
  } catch (error) {
    console.error(`❌ Errore aggiornamento rapportino ${rapportino.id}:`, error);
    throw error;
  }
};

/**
 * Resetta un rapportino alle ore originali
 */
const resetRapportinoToOriginal = async (rapportino, userId, originalHours) => {
  try {
    const updatedPersonale = rapportino.personalePresente.map(persona => {
      if (persona.personaId !== userId) return persona;
      
      const costoOrario = parseFloat(persona.costoOrario) || 0;
      const nuovoTotale = originalHours.oreTotali * costoOrario;
      
      let notePersonale = `${originalHours.oreStandard}h standard`;
      if (originalHours.oreStraordinario > 0) {
        notePersonale += ` + ${originalHours.oreStraordinario}h straordinario`;
      }
      
      return {
        ...persona,
        oreLavorate: originalHours.oreTotali,
        oreStandard: originalHours.oreStandard,
        oreStraordinario: originalHours.oreStraordinario,
        totale: nuovoTotale,
        notePersonale: notePersonale,
        distributedHours: false,
        distributionDetails: null
      };
    });
    
    // Verifica se ci sono altre persone con distribuzione
    const hasOtherDistributions = updatedPersonale.some(p => 
      p.personaId !== userId && p.distributedHours === true
    );
    
    const updatedRapportino = {
      ...rapportino,
      personalePresente: updatedPersonale,
      distributedHoursUpdate: hasOtherDistributions,
      hasDistributedHours: hasOtherDistributions,
      distributionMetadata: hasOtherDistributions ? {
        ...rapportino.distributionMetadata,
        affectedPersonIds: (rapportino.distributionMetadata?.affectedPersonIds || [])
          .filter(id => id !== userId)
      } : null
    };
    
    await updateDoc(doc(db, 'rapportini', rapportino.id), updatedRapportino);
    console.log(`✅ Reset rapportino ${rapportino.id} alle ore originali`);
    
  } catch (error) {
    console.error(`❌ Errore reset rapportino ${rapportino.id}:`, error);
    throw error;
  }
};

/**
 * Aggiorna tutti i rapportini in batch
 */
const updateRapportiniBatch = async (updates) => {
  try {
    const batch = writeBatch(db);
    
    for (const update of updates) {
      const { rapportinoId, data } = update;
      const rapportinoRef = doc(db, 'rapportini', rapportinoId);
      batch.update(rapportinoRef, data);
    }
    
    await batch.commit();
    console.log(`✅ Aggiornati ${updates.length} rapportini in batch`);
    
  } catch (error) {
    console.error('❌ Errore aggiornamento batch:', error);
    throw error;
  }
};

// =====================================================
// 🎯 FUNZIONI PRINCIPALI AUTOMATICHE
// =====================================================

/**
 * Distribuzione automatica delle ore
 */
const performAutomaticDistribution = async (userId, date, rapportini, originalHours) => {
  try {
    console.log(`🔄 Inizio distribuzione automatica per ${rapportini.length} cantieri`);
    
    const distributedHours = calculateOptimalDistribution(
      originalHours.oreStandard,
      originalHours.oreStraordinario,
      rapportini.length
    );
    
    // Aggiorna tutti i rapportini
    const updatePromises = rapportini.map((rapportino, index) => 
      updateRapportinoWithDistribution(rapportino, userId, distributedHours, index)
    );
    
    await Promise.all(updatePromises);
    
    console.log(`✅ Distribuzione completata per ${rapportini.length} cantieri`);
    
    return {
      success: true,
      action: 'distributed',
      numCantieri: rapportini.length,
      distributedHours: distributedHours
    };
    
  } catch (error) {
    console.error('❌ Errore distribuzione automatica:', error);
    throw error;
  }
};

/**
 * Reset automatico alle ore originali
 */
const performAutomaticReset = async (userId, date, rapportini, originalHours) => {
  try {
    console.log(`🔄 Inizio reset automatico per ${rapportini.length} cantieri`);
    
    // Reset tutti i rapportini
    const resetPromises = rapportini.map(rapportino => 
      resetRapportinoToOriginal(rapportino, userId, originalHours)
    );
    
    await Promise.all(resetPromises);
    
    console.log(`✅ Reset completato per ${rapportini.length} cantieri`);
    
    return {
      success: true,
      action: 'reset',
      numCantieri: rapportini.length
    };
    
  } catch (error) {
    console.error('❌ Errore reset automatico:', error);
    throw error;
  }
};

// =====================================================
// 🚀 FUNZIONE PRINCIPALE AUTOMATICA
// =====================================================

/**
 * 🎯 SISTEMA AUTOMATICO DI DISTRIBUZIONE ORE
 * 
 * Gestisce automaticamente tutte le operazioni di distribuzione:
 * - Analizza la situazione corrente
 * - Decide l'azione necessaria
 * - Esegue l'operazione appropriata
 * - Gestisce errori e recovery
 * 
 * @param {string} userId - ID della persona
 * @param {string} date - Data nel formato YYYY-MM-DD
 * @param {Object} options - Opzioni aggiuntive
 * @returns {Object} Risultato dell'operazione
 */
export const processAutomaticHoursDistribution = async (userId, date, options = {}) => {
  const startTime = Date.now();
  
  try {
    console.log(`🚀 INIZIO PROCESSO AUTOMATICO per ${userId} data ${date}`);
    
    // 1. Analizza la situazione
    const analysis = await analyzeDistributionSituation(userId, date);
    console.log(`📊 Analisi:`, analysis);
    
    // 2. Se non serve fare nulla
    if (analysis.action === 'none') {
      return {
        success: true,
        action: 'none',
        message: analysis.message,
        processingTime: Date.now() - startTime
      };
    }
    
    // 3. Recupera le ore originali
    const originalHours = await getOriginalHours(userId, date);
    if (!originalHours) {
      return {
        success: false,
        action: 'error',
        message: 'Impossibile recuperare le ore originali dal workHours',
        processingTime: Date.now() - startTime
      };
    }
    
    // 4. Esegui l'azione necessaria
    let result;
    
    if (analysis.action === 'distribute') {
      result = await performAutomaticDistribution(
        userId, 
        date, 
        analysis.rapportini, 
        originalHours
      );
    } else if (analysis.action === 'reset') {
      result = await performAutomaticReset(
        userId, 
        date, 
        analysis.rapportini, 
        originalHours
      );
    }
    
    // 5. Risultato finale
    const processingTime = Date.now() - startTime;
    
    console.log(`✅ PROCESSO COMPLETATO in ${processingTime}ms`);
    
    return {
      ...result,
      originalHours,
      analysis,
      processingTime,
      details: {
        cantieri: analysis.rapportini.map(r => ({
          id: r.id,
          nomeCommessa: r.nomeCommessa || r.cantiereId,
          oreAssegnate: result.distributedHours ? 
            result.distributedHours.distribuzioneOre[analysis.rapportini.indexOf(r)]?.oreTotali : 
            originalHours.oreTotali
        }))
      }
    };
    
  } catch (error) {
    console.error('❌ ERRORE PROCESSO AUTOMATICO:', error);
    
    return {
      success: false,
      action: 'error',
      message: error.message,
      error: error,
      processingTime: Date.now() - startTime
    };
  }
};

// =====================================================
// 🔄 TRIGGER AUTOMATICI
// =====================================================

/**
 * Trigger dopo aggiunta persona a rapportino
 */
export const triggerAfterPersonaAdded = async (userId, date, delay = 1000) => {
  console.log(`🔄 Trigger aggiunta persona: ${userId} data ${date}`);
  
  // Piccolo delay per permettere il salvataggio
  await new Promise(resolve => setTimeout(resolve, delay));
  
  return await processAutomaticHoursDistribution(userId, date);
};

/**
 * Trigger dopo rimozione persona da rapportino
 */
export const triggerAfterPersonaRemoved = async (userId, date, delay = 500) => {
  console.log(`🔄 Trigger rimozione persona: ${userId} data ${date}`);
  
  // Piccolo delay per permettere il salvataggio
  await new Promise(resolve => setTimeout(resolve, delay));
  
  return await processAutomaticHoursDistribution(userId, date);
};

/**
 * Trigger dopo modifica workHours
 */
export const triggerAfterWorkHoursChange = async (userId, date, delay = 1000) => {
  console.log(`🔄 Trigger modifica workHours: ${userId} data ${date}`);
  
  // Delay maggiore per permettere la sincronizzazione
  await new Promise(resolve => setTimeout(resolve, delay));
  
  return await processAutomaticHoursDistribution(userId, date);
};

/**
 * Trigger batch per più persone
 */
export const triggerBatchDistribution = async (userIds, date, delay = 2000) => {
  console.log(`🔄 Trigger batch per ${userIds.length} persone data ${date}`);
  
  await new Promise(resolve => setTimeout(resolve, delay));
  
  const results = {};
  
  for (const userId of userIds) {
    try {
      results[userId] = await processAutomaticHoursDistribution(userId, date);
      // Piccola pausa tra un utente e l'altro
      await new Promise(resolve => setTimeout(resolve, 200));
    } catch (error) {
      console.error(`❌ Errore batch per ${userId}:`, error);
      results[userId] = {
        success: false,
        action: 'error',
        message: error.message
      };
    }
  }
  
  return results;
};

// =====================================================
// 🛠️ FUNZIONI DI UTILITÀ
// =====================================================

/**
 * Verifica lo stato di distribuzione per una persona
 */
export const checkDistributionStatus = async (userId, date) => {
  try {
    const analysis = await analyzeDistributionSituation(userId, date);
    
    return {
      userId,
      date,
      numCantieri: analysis.rapportini.length,
      needsDistribution: analysis.needsDistribution,
      needsReset: analysis.needsReset,
      hasIncompleteDistribution: analysis.hasIncompleteDistribution,
      rapportini: analysis.rapportini.map(r => ({
        id: r.id,
        nomeCommessa: r.nomeCommessa || r.cantiereId,
        hasDistribution: r.personaData.distributedHours === true,
        oreAssegnate: r.personaData.oreLavorate
      }))
    };
  } catch (error) {
    console.error('❌ Errore verifica stato distribuzione:', error);
    return {
      userId,
      date,
      error: error.message,
      numCantieri: 0,
      needsDistribution: false,
      needsReset: false
    };
  }
};

/**
 * Forza la ridistribuzione per una persona
 */
export const forceRedistribution = async (userId, date) => {
  console.log(`🔄 Forzatura ridistribuzione per ${userId} data ${date}`);
  
  return await processAutomaticHoursDistribution(userId, date, {
    force: true
  });
};

/**
 * Pulisce tutte le distribuzioni per una data
 */
export const cleanAllDistributionsForDate = async (date) => {
  try {
    console.log(`🧹 Pulizia distribuzioni per data ${date}`);
    
    const rapportiniRef = collection(db, 'rapportini');
    const q = query(rapportiniRef, where('data', '==', date));
    const querySnapshot = await getDocs(q);
    
    const cleanupPromises = [];
    
    querySnapshot.forEach((doc) => {
      const rapportino = doc.data();
      const hasDistributions = rapportino.personalePresente?.some(p => p.distributedHours === true);
      
      if (hasDistributions) {
        const cleanedPersonale = rapportino.personalePresente.map(persona => ({
          ...persona,
          distributedHours: false,
          distributionDetails: null,
          notePersonale: persona.notePersonale?.includes('Ore distribuite') ? 
            `${persona.oreStandard}h standard` + (persona.oreStraordinario > 0 ? ` + ${persona.oreStraordinario}h straordinario` : '') :
            persona.notePersonale
        }));
        
        cleanupPromises.push(
          updateDoc(doc.ref, {
            personalePresente: cleanedPersonale,
            distributedHoursUpdate: false,
            hasDistributedHours: false,
            distributionMetadata: null
          })
        );
      }
    });
    
    await Promise.all(cleanupPromises);
    
    console.log(`✅ Pulite ${cleanupPromises.length} distribuzioni`);
    
    return {
      success: true,
      cleanedRapportini: cleanupPromises.length
    };
    
  } catch (error) {
    console.error('❌ Errore pulizia distribuzioni:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// =====================================================
// 🎯 EXPORT CORRETTI
// =====================================================

// 🔧 FIX: Esporta SOLO le funzioni che non sono già esportate
export {
  // Funzioni di ricerca (FIX PRINCIPALE) - queste NON sono esportate sopra
  findRapportiniWithEmployee,
  findAllRapportiniWithEmployee,
  
  // Configurazione
  DISTRIBUTION_CONFIG
};

// Export default per compatibilità
export default {
  // Funzioni di ricerca (FIX PRINCIPALE)
  findRapportiniWithEmployee,
  findAllRapportiniWithEmployee,
  
  // Funzione principale
  processAutomaticHoursDistribution,
  
  // Triggers
  triggerAfterPersonaAdded,
  triggerAfterPersonaRemoved,
  triggerAfterWorkHoursChange,
  triggerBatchDistribution,
  
  // Utilità
  checkDistributionStatus,
  forceRedistribution,
  cleanAllDistributionsForDate,
  
  // Configurazione
  DISTRIBUTION_CONFIG
};